import type { Timestamp } from "firebase-admin/firestore";
import { z } from "zod";

/** Duck-typed Timestamp check (Admin or client SDK). Avoid `instanceof` across SDKs. */
const zTimestamp = z.custom<Timestamp>(
  (val) => Boolean(val) && typeof (val as { toDate?: unknown }).toDate === "function",
  {
    error: "Expected Firestore Timestamp",
  }
);

/** Maps one field's schema type: date wrappers → Timestamp, everything else unchanged. */
// biome-ignore format: flat ternary chain mirrors convertDateField's branches
type DateFieldToTimestamp<F> =
  F extends z.ZodDate ? z.ZodType<Timestamp> :
  F extends z.ZodOptional<z.ZodDate> ? z.ZodOptional<z.ZodType<Timestamp>> :
  F extends z.ZodNullable<z.ZodDate> ? z.ZodNullable<z.ZodType<Timestamp>> :
  F extends z.ZodNonOptional<z.ZodDate> ? z.ZodNonOptional<z.ZodType<Timestamp>> :
  F extends z.ZodDefault<z.ZodDate> ? z.ZodDefault<z.ZodType<Timestamp>> :
  F;

type DateFieldsToTimestamps<T extends z.ZodRawShape> = {
  [K in keyof T]: DateFieldToTimestamp<T[K]>;
};

function isNullableDate(schema: z.ZodType): boolean {
  if (schema instanceof z.ZodNullable) {
    return schema.unwrap() instanceof z.ZodDate;
  }
  return false;
}

function convertDateField(value: z.ZodType): z.ZodType {
  if (value instanceof z.ZodDefault) {
    const inner = convertDateField(value.unwrap() as z.ZodType);
    const defaultValue = value.def.defaultValue;
    return inner.default(defaultValue);
  }
  if (value instanceof z.ZodNonOptional) {
    return convertDateField(value.unwrap() as z.ZodType).nonoptional();
  }
  if (value instanceof z.ZodOptional) {
    return convertDateField(value.unwrap() as z.ZodType).optional();
  }
  if (isNullableDate(value)) {
    return zTimestamp.nullable();
  }
  if (value instanceof z.ZodDate) {
    return zTimestamp;
  }
  return value;
}

/**
 * Derive a Firestore record schema from a client schema that uses `z.date()`.
 * Top-level date fields — plain, optional, nullable, non-optional, default —
 * become Timestamp. Everything else passes through unchanged.
 *
 * Not converted (by design; extend those fields by hand if they ever hold dates):
 * - Nested object / array dates
 * - Dates behind `.catch(...)` (`ZodCatch`) or `.transform(...)` (`ZodPipe` in Zod 4)
 *
 * A date behind an unhandled wrapper stays `z.date()` in the record schema and will
 * reject Admin SDK Timestamps at parse time.
 */
export function toFirestoreSchema<T extends z.ZodRawShape>(
  schema: z.ZodObject<T>
): z.ZodObject<DateFieldsToTimestamps<T>> {
  const newShape = Object.fromEntries(
    Object.entries(schema.shape).map(([key, value]) => [key, convertDateField(value as z.ZodType)])
  );

  return z.object(newShape) as z.ZodObject<DateFieldsToTimestamps<T>>;
}

export default toFirestoreSchema;
